import { useCallback, useRef } from 'react';
import { unwrapResult } from '@reduxjs/toolkit';
import { useAppDispatch } from './storeHooks';
import { useToast } from '../context/ToasterContext';
import { store } from '../store';
import { archiveVendorChats, unarchiveVendorChats } from '../store/chats/chatsThunk';
import { patchChatState } from '../store/chats/chatsSlice';
import { fetchTeamMemberSendbirdSessionThunk } from '../store/user/userThunk';
import { IChatStateResponse, IVendorChatTarget } from '../interfaces/chats';
import { iApiError } from '../types/commonTypes';
import { REACT_APP_SENDBIRD_ID } from '../constants/envConstants';
import { isSendbirdSessionExpired, toConnectionCredentials } from '../helpers/sendbird';
import { markChannelsRead, markChannelsUnread } from '../utils/sendbird/markChannelsUnread';
import { messagingDocTimestampMillis } from './firestoreHooks/serializeMessagingDocTimestamp';

export interface iArchiveRequest {
  eventId: string;
  targets: IVendorChatTarget[];
  message: string;
  isEvent: boolean;
}

interface iReadStateChat {
  id: string;
  channelUrl: string;
  unreadMessageCount?: number | null;
  hasUnreadMention?: boolean;
}

interface iUseChatListActionsOptions {
  onArchiveChange: (request: iArchiveRequest, archived: boolean) => void;
  onArchiveSettled: (request: iArchiveRequest, archived: boolean, succeeded: boolean) => void;
  onChatStateChange: () => void;
  onReadStateChange: () => void;
}

const errorMessage = (error: unknown, fallback: string) => (error as iApiError | undefined)?.message || fallback;

const useChatListActions = ({
  onArchiveChange,
  onArchiveSettled,
  onChatStateChange,
  onReadStateChange,
}: iUseChatListActionsOptions) => {
  const dispatch = useAppDispatch();
  const { showToast, showPromiseToast } = useToast();
  const ownChatStateRef = useRef({ inFlight: 0, stamps: new Set<number>(), missedSignal: false });
  const onArchiveChangeRef = useRef(onArchiveChange);
  onArchiveChangeRef.current = onArchiveChange;
  const onArchiveSettledRef = useRef(onArchiveSettled);
  onArchiveSettledRef.current = onArchiveSettled;
  const onChatStateChangeRef = useRef(onChatStateChange);
  onChatStateChangeRef.current = onChatStateChange;
  const onReadStateChangeRef = useRef(onReadStateChange);
  onReadStateChangeRef.current = onReadStateChange;

  const trackChatStateRequest = useCallback(async <T extends IChatStateResponse>(request: Promise<T>) => {
    const own = ownChatStateRef.current;
    own.inFlight += 1;
    let succeeded = false;
    try {
      const response = await request;
      succeeded = true;
      const stamp = messagingDocTimestampMillis(response?.chatStateUpdatedAt);
      if (stamp !== null) own.stamps.add(stamp);
      return response;
    } finally {
      own.inFlight -= 1;
      if (!own.inFlight) {
        const missedSignal = own.missedSignal;
        own.missedSignal = false;
        if (missedSignal && !succeeded) onChatStateChangeRef.current();
      }
    }
  }, []);

  const isOwnChatStateSignal = useCallback((chatStateUpdatedAt: number | null) => {
    const own = ownChatStateRef.current;
    if (chatStateUpdatedAt !== null && own.stamps.delete(chatStateUpdatedAt)) return true;
    if (!own.inFlight) return false;
    own.missedSignal = true;
    return true;
  }, []);

  const setArchived = useCallback(
    async (request: iArchiveRequest, archived: boolean, isUndo = false) => {
      const plural = request.isEvent ? 'Chats' : 'Chat';
      onArchiveChangeRef.current(request, archived);
      const pending: Promise<iArchiveRequest> = (
        archived
          ? trackChatStateRequest(dispatch(archiveVendorChats(request.targets)).then(unwrapResult))
          : trackChatStateRequest(dispatch(unarchiveVendorChats(request.targets)).then(unwrapResult))
      ).then(() => request);

      showPromiseToast(pending, {
        loading: {
          title: archived ? `Archiving ${plural.toLowerCase()}…` : `Unarchiving ${plural.toLowerCase()}…`,
          message: request.message,
          action: isUndo ? undefined : { label: 'Undo', onClick: () => undefined, disabled: true },
        },
        success: (applied) => ({
          title: archived ? `${plural} archived` : `${plural} unarchived`,
          message: applied.message,
          action: isUndo ? undefined : { label: 'Undo', onClick: () => void setArchived(applied, !archived, true) },
        }),
        error: (error) => ({
          message: errorMessage(error, archived ? "Couldn't archive. Please try again." : "Couldn't unarchive. Please try again."),
        }),
      });

      try {
        onArchiveSettledRef.current(await pending, archived, true);
      } catch {
        onArchiveSettledRef.current(request, archived, false);
      }
    },
    [dispatch, showPromiseToast, trackChatStateRequest]
  );

  const archive = useCallback((request: iArchiveRequest) => setArchived(request, true), [setArchived]);
  const unarchive = useCallback((request: iArchiveRequest) => setArchived(request, false), [setArchived]);

  const getTeamMemberCredentials = useCallback(async () => {
    const { teamMemberSendbirdSession, sendbirdApplicationId } = store.getState().user;
    let session = teamMemberSendbirdSession;
    if (!session || isSendbirdSessionExpired(session.expiresAt)) {
      session = unwrapResult(await dispatch(fetchTeamMemberSendbirdSessionThunk({ force: true })));
    }
    const credentials = toConnectionCredentials(session);
    return {
      ...credentials,
      applicationId: sendbirdApplicationId || credentials.applicationId || REACT_APP_SENDBIRD_ID,
    };
  }, [dispatch]);

  const markUnread = useCallback(
    async (chats: iReadStateChat[]) => {
      if (!chats.length) return false;
      let results;
      try {
        results = await markChannelsUnread(
          await getTeamMemberCredentials(),
          chats.map((chat) => chat.channelUrl)
        );
      } catch {
        showToast('Unable to mark as unread for this chat', undefined, 'error');
        return false;
      }
      const byUrl = new Map(results.map((result) => [result.channelUrl, result]));
      chats.forEach((chat) => {
        const result = byUrl.get(chat.channelUrl);
        if (result?.outcome === 'marked' || result?.outcome === 'already-unread') {
          dispatch(patchChatState({ chatIds: [chat.id], patch: { unreadMessageCount: result.unreadMessageCount } }));
        }
      });
      const outcomes = results.map((result) => result.outcome);
      if (outcomes.some((outcome) => outcome === 'marked' || outcome === 'already-unread')) {
        onReadStateChangeRef.current();
        return true;
      }
      if (outcomes.includes('unavailable')) {
        showToast('Chats are still connecting. Please try again in a moment.', undefined, 'info');
      } else if (outcomes.includes('failed')) {
        showToast('Error marking as unread', undefined, 'error');
      } else if (outcomes.includes('own-message')) {
        showToast(
          chats.length === 1 ? 'You cannot mark your own message as unread' : 'The last message in these chats is yours',
          undefined,
          'info'
        );
      } else {
        showToast('No messages to mark as unread', undefined, 'info');
      }
      return false;
    },
    [dispatch, getTeamMemberCredentials, showToast]
  );

  const markRead = useCallback(
    async (chats: iReadStateChat[]) => {
      const unreadChats = chats.filter((chat) => chat.unreadMessageCount || chat.hasUnreadMention);
      if (!unreadChats.length) return false;
      const restore = (chat: iReadStateChat) =>
        dispatch(
          patchChatState({
            chatIds: [chat.id],
            patch: { unreadMessageCount: chat.unreadMessageCount ?? 0, hasUnreadMention: chat.hasUnreadMention ?? false },
          })
        );
      dispatch(
        patchChatState({
          chatIds: unreadChats.map((chat) => chat.id),
          patch: { unreadMessageCount: 0, hasUnreadMention: false },
        })
      );

      let results;
      try {
        results = await markChannelsRead(
          await getTeamMemberCredentials(),
          unreadChats.map((chat) => chat.channelUrl)
        );
      } catch {
        unreadChats.forEach(restore);
        showToast('Unable to mark as read for this chat', undefined, 'error');
        return false;
      }
      const byUrl = new Map(results.map((result) => [result.channelUrl, result]));
      unreadChats.forEach((chat) => {
        const outcome = byUrl.get(chat.channelUrl)?.outcome;
        if (outcome !== 'marked' && outcome !== 'already-read') restore(chat);
      });
      const outcomes = results.map((result) => result.outcome);
      if (outcomes.some((outcome) => outcome === 'marked' || outcome === 'already-read')) {
        onReadStateChangeRef.current();
        return true;
      }
      if (outcomes.includes('unavailable')) {
        showToast('Chats are still connecting. Please try again in a moment.', undefined, 'info');
      } else {
        showToast('Error marking as read', undefined, 'error');
      }
      return false;
    },
    [dispatch, getTeamMemberCredentials, showToast]
  );

  return { archive, unarchive, markUnread, markRead, isOwnChatStateSignal };
};

export default useChatListActions;
